import { DOCUMENT } from "@angular/common";
import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	EventEmitter,
	HostBinding,
	Inject,
	Input,
	isDevMode,
	Output,
	QueryList,
	ViewChildren,
	ViewEncapsulation,
} from "@angular/core";
import {
	AbstractControl,
	ControlValueAccessor,
	FormArray,
	FormControl,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ValidationErrors,
	Validator,
} from "@angular/forms";
import { noop } from "lodash-es";

@Component({
	selector: "obg-verification-code",
	templateUrl: "./verification-code.component.html",
	styleUrls: ["./verification-code.component.scss"],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: VerificationCodeComponent,
			multi: true,
		},
		{
			provide: NG_VALIDATORS,
			useExisting: VerificationCodeComponent,
			multi: true,
		},
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.Emulated,
})
export class VerificationCodeComponent implements ControlValueAccessor, Validator {
	@Input()
	set size(size: number) {
		this.inputs = getFormArray(size);
		this._size = size;
	}
	private _size = 6;

	@Output()
	enterPress = new EventEmitter<KeyboardEvent>();

	/**
	 * Emitted when the user types a number in the last input,
	 * and all previous fields are filled as well.
	 */
	@Output()
	complete = new EventEmitter<KeyboardEvent>();

	/**
	 * Emitted when the user presses backspace or delete
	 * after the code was complete
	 */
	@Output()
	incomplete = new EventEmitter<KeyboardEvent>();

	/**
	 * Emitted when the component and all of it's inputs lose focus.
	 */
	@Output()
	blur = new EventEmitter<void>();

	@ViewChildren("inputElement")
	inputElements!: QueryList<ElementRef<HTMLInputElement>>;

	inputs = getFormArray(this._size);

	onChange: (value: string) => void = noop;
	onTouched: () => void = noop;

	@HostBinding("class.filled") filled = false;

	private scheduledFocus: number | null = null;

	constructor(@Inject(DOCUMENT) private document: Document) {}

	writeValue(value: string): void {
		if (isDevMode() && value?.length) {
			throw new Error("Otp input is not supposed to be prefilled with data");
		}

		this.inputs.setValue(new Array<string>(this._size).fill(""));
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		if (isDisabled) {
			this.inputs.disable();
		} else {
			this.inputs.enable();
		}
	}

	validate(control: AbstractControl<string, string>): ValidationErrors | null {
		if (!control.value || control.value.length < this._size) {
			return {
				otpInputInvalidFormat: true,
			};
		}

		return null;
	}

	handleBackspaceOrDelete(inputIndex: number, event: KeyboardEvent) {
		const isFirstInput = inputIndex === 0;
		if (isFirstInput) {
			return;
		}
		const newlyFocusedIndex = inputIndex - 1;
		const inputHasValue = this.inputs.controls[inputIndex].value !== "";
		if (inputHasValue) {
			this.inputs.controls[inputIndex].setValue("");
		} else {
			this.inputs.controls[newlyFocusedIndex].setValue("");
			this.focusInput(newlyFocusedIndex);
		}
		this.incomplete.emit(event);
		this.updateWiredValue();
	}

	handleArrowLeft(inputIndex: number) {
		const isFirstInput = inputIndex === 0;
		if (isFirstInput) {
			return;
		}
		const newlyFocusedIndex = inputIndex - 1;
		this.focusInput(newlyFocusedIndex);
	}

	handleArrowRight(inputIndex: number) {
		const isLastInput = inputIndex === this._size - 1;
		if (isLastInput) {
			return;
		}
		const newlyFocusedIndex = inputIndex + 1;
		this.focusInput(newlyFocusedIndex);
	}

	// Due to iOS/iPadOS Safari bug/special behavior we are forced to
	// schedule focus transition during keypress/keydown event and only
	// after input event happened - execute the transition
	// otherwise inputs don't get their values filled
	handleInput() {
		this.updateWiredValue();

		if (this.scheduledFocus != null) {
			this.focusInput(this.scheduledFocus);
			this.scheduledFocus = null;
		}
	}

	handleKeyPress(event: KeyboardEvent, idx: number) {
		const { key, metaKey } = event;
		const isDigit = /\d/.test(key);

		// Safari fires Cmd + V through keyPress event as well
		// so we need to handle it here and let it through
		if (key === "v" && metaKey) {
			return true;
		}

		if (!isDigit) {
			return false;
		}

		if (this.getNumberOfFilledInputs() === this._size - 1) {
			this.complete.emit(event);
		}

		if (idx + 1 < this._size) {
			// If user inputs digits & we are not on the last input we want
			// to advance the focus
			this.scheduledFocus = idx + 1;
		}

		if (this.inputs.controls[idx].value) {
			// If user deselects an input which already has a value
			// we want to clear it so that it doesn't have more than 1 digit
			this.inputs.controls[idx].setValue("");
		}

		return true;
	}

	submitEnterPress(event: KeyboardEvent) {
		this.enterPress.emit(event);
	}

	handlePaste(event: ClipboardEvent, inputIndex: number) {
		event.preventDefault();

		if (inputIndex !== 0) {
			return;
		}

		const pasteData = event.clipboardData?.getData("text").trim() ?? "";
		this.fillInput(pasteData);
	}

	handleOtpFill(otpCode: string) {
		this.fillInput(otpCode);
	}

	fillInput(data: string) {
		const validatorRegex = new RegExp(`^\\d{${this._size}}$`);
		const dataDoesNotMatchRequiredFormat = !validatorRegex.test(data);

		if (dataDoesNotMatchRequiredFormat) {
			return;
		}

		for (let i = 0; i < data.length; i++) {
			this.inputs.controls[i].setValue(data[i]);
		}

		this.focusInput(this.inputElements.length - 1);
		this.updateWiredValue();
		this.onTouched();
	}

	handleFocus({ target }: FocusEvent) {
		(target as HTMLInputElement).select();
	}

	trackByIndex(index: number): number {
		return index;
	}

	focus() {
		this.focusInput(0);
	}

	handleBlur() {
		this.onTouched();

		setTimeout(() => {
			if (this.areEveryInputUnfocused()) {
				this.blur.emit();
			}
		});
	}
	private areEveryInputUnfocused() {
		return this.inputElements
			.map((element) => element.nativeElement)
			.every((input) => this.document.activeElement !== input);
	}

	private focusInput(idx: number) {
		// In order not to interfere with the input we setTimeout
		// before advancing the focus
		setTimeout(() => this.inputElements.get(idx)?.nativeElement.focus());
	}

	private updateWiredValue() {
		// In order not to interfere with the input we setTimeout
		// before advancing the focus
		const valuesConcatenated = this.getConcatenatedValues();
		setTimeout(() => {
			this.onChange(valuesConcatenated);
			this.filled = valuesConcatenated.length === this._size;
		});
	}

	private getConcatenatedValues() {
		return this.inputs.value.join("");
	}

	private getNumberOfFilledInputs() {
		return this.getConcatenatedValues().length;
	}
}

function getFormArray(size: number) {
	const arr = [];

	for (let i = 0; i < size; i++) {
		arr.push(new FormControl(""));
	}

	return new FormArray(arr);
}
